import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Package,
  ShoppingCart,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Check,
  Clock,
  Truck,
  X,
  Edit,
  Plus,
  Minus,
  LogOut,
  BarChart3,
  Trash2,
  FolderPlus,
  Save,
  ChevronDown,
  ChevronRight,
  MoveRight,
  Folder,
  RefreshCw,
  Cloud,
  Wand2,
  Settings as SettingsIcon,
} from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Data states
  const [analytics, setAnalytics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  // Modal states
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showEditProduct, setShowEditProduct] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showMoveProduct, setShowMoveProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [movingProduct, setMovingProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Form states
  const [newProduct, setNewProduct] = useState({
    name: "",
    description: "",
    price: "",
    category: "",
    image_url: "",
    stock: 10,
    featured: false,
  });
  const [newCategory, setNewCategory] = useState({
    name: "",
    slug: "",
    description: "",
  });
  const [moveToCategory, setMoveToCategory] = useState("");

  // Clover sync states
  const [syncStatus, setSyncStatus] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [fetchingImageId, setFetchingImageId] = useState(null);
  const [isBulkFetching, setIsBulkFetching] = useState(false);
  const [settings, setSettings] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState({});
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isClearingOrders, setIsClearingOrders] = useState(false);
  const [franposSync, setFranposSync] = useState(null);
  const [franposQuota, setFranposQuota] = useState(null);

  // Check if already authenticated
  useEffect(() => {
    const savedPassword = localStorage.getItem("airo_admin_password");
    if (savedPassword) {
      verifyPassword(savedPassword);
    }
  }, []);

  const verifyPassword = async (pwd) => {
    try {
      const response = await axios.post(`${API}/admin/login`, { password: pwd });
      if (response.data.success) {
        setIsAuthenticated(true);
        setAdminPassword(pwd);
        localStorage.setItem("airo_admin_password", pwd);
        fetchAllData(pwd);
      }
    } catch (e) {
      localStorage.removeItem("airo_admin_password");
      toast.error("Invalid password");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    await verifyPassword(password);
    setLoading(false);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAdminPassword("");
    localStorage.removeItem("airo_admin_password");
  };

  const fetchAllData = async (pwd) => {
    try {
      const headers = { "X-Admin-Password": pwd };
      const [analyticsRes, ordersRes, productsRes, categoriesRes, syncStatusRes, settingsRes, franposStatusRes, franposQuotaRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`, { headers }),
        axios.get(`${API}/admin/orders`, { headers }),
        axios.get(`${API}/admin/products`, { headers }),
        axios.get(`${API}/admin/categories`, { headers }),
        axios.get(`${API}/admin/sync-status`, { headers }),
        axios.get(`${API}/admin/settings`, { headers }),
        axios.get(`${API}/admin/sync-franpos/status`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/franpos/quota`, { headers }).catch(() => ({ data: null })),
      ]);
      setAnalytics(analyticsRes.data);
      setOrders(ordersRes.data);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setSyncStatus(syncStatusRes.data);
      setSettings(settingsRes.data);
      setFranposSync(franposStatusRes.data);
      setFranposQuota(franposQuotaRes.data);
      const draft = {};
      Object.entries(settingsRes.data).forEach(([k, meta]) => {
        draft[k] = meta.sensitive ? "" : meta.value;
      });
      setSettingsDraft(draft);
    } catch (e) {
      console.error("Error fetching admin data:", e);
      toast.error("Failed to fetch data");
    }
  };

  const handleCloverSync = async () => {
    setIsSyncing(true);
    try {
      const response = await axios.post(
        `${API}/admin/sync-clover`,
        {},
        { headers: { "X-Admin-Password": adminPassword } }
      );
      if (response.data.success) {
        const { categories: catResults, products: prodResults } = response.data.results;
        toast.success(
          `Sync completed! Categories: ${catResults.added} added, ${catResults.updated} updated. Products: ${prodResults.added} added, ${prodResults.updated} updated.`
        );
        fetchAllData(adminPassword);
      }
    } catch (e) {
      console.error("Clover sync error:", e);
      toast.error(e.response?.data?.detail || "Failed to sync with Clover");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleAutoFetchImage = async (productId) => {
    setFetchingImageId(productId);
    try {
      const response = await axios.post(
        `${API}/admin/products/${productId}/auto-image`,
        {},
        { headers: { "X-Admin-Password": adminPassword } }
      );
      if (response.data.success) {
        toast.success("Image fetched from Google");
        fetchAllData(adminPassword);
      }
    } catch (e) {
      console.error("Auto-fetch image error:", e);
      toast.error(e.response?.data?.detail || "Failed to fetch image");
    } finally {
      setFetchingImageId(null);
    }
  };

  const handleAutoFetchAllImages = async () => {
    if (!window.confirm(
      "Fetch images from Google for all products without a custom image? This may take a few minutes and consumes Google Custom Search quota."
    )) return;
    setIsBulkFetching(true);
    try {
      const response = await axios.post(
        `${API}/admin/products/auto-fetch-images`,
        {},
        { headers: { "X-Admin-Password": adminPassword }, timeout: 600000 }
      );
      const { updated, skipped, failed, total, error } = response.data;
      if (error) {
        toast.error(`Stopped after error: ${error}`);
      } else {
        toast.success(
          `Done. Updated ${updated} of ${total} products. Skipped (already had image): ${skipped}. Failed: ${failed}.`
        );
      }
      fetchAllData(adminPassword);
    } catch (e) {
      console.error("Bulk auto-fetch error:", e);
      toast.error(e.response?.data?.detail || "Bulk image fetch failed");
    } finally {
      setIsBulkFetching(false);
    }
  };

  const toggleCategoryVisibility = async (categoryId, hidden) => {
    try {
      await axios.put(
        `${API}/admin/categories/${categoryId}`,
        { hidden },
        { headers: { "X-Admin-Password": adminPassword } }
      );
      toast.success(hidden ? "Category hidden from website" : "Category visible on website");
      fetchAllData(adminPassword);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update category visibility");
    }
  };

  const handleClearAllOrders = async () => {
    const total = orders.length;
    if (total === 0) return;
    const confirmText = window.prompt(
      `This will PERMANENTLY DELETE all ${total} orders. This cannot be undone.\n\nType "DELETE" (in caps) to confirm:`
    );
    if (confirmText !== "DELETE") {
      toast.info("Cancelled — no orders were deleted");
      return;
    }
    setIsClearingOrders(true);
    try {
      const response = await axios.delete(`${API}/admin/orders/all`, {
        headers: { "X-Admin-Password": adminPassword },
      });
      toast.success(`Deleted ${response.data.deleted} orders`);
      fetchAllData(adminPassword);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clear orders");
    } finally {
      setIsClearingOrders(false);
    }
  };


  const fetchFranposQuota = async () => {
    try {
      const r = await axios.get(`${API}/admin/franpos/quota`, {
        headers: { "X-Admin-Password": adminPassword },
      });
      setFranposQuota(r.data);
    } catch (e) {
      // ignore
    }
  };

  const pollFranposSyncStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/sync-franpos/status`, {
        headers: { "X-Admin-Password": adminPassword },
      });
      setFranposSync(r.data);
      if (!r.data.running) {
        if (r.data.error) toast.error(`Sync error: ${r.data.error}`);
        else if (r.data.finished_at) {
          toast.success(
            `Franpos sync done — ${r.data.synced} products, ${r.data.categories_synced} categories`
          );
        }
        fetchAllData(adminPassword);
        fetchFranposQuota();
        return true; // stop polling
      }
    } catch (e) {
      // ignore polling errors
    }
    return false;
  };

  const handleFranposSync = async (wipeFirst) => {
    if (wipeFirst) {
      const confirmMsg = window.prompt(
        'This will DELETE all existing products and categories, then re-import from Franpos.\nType "WIPE" (in caps) to confirm:'
      );
      if (confirmMsg !== "WIPE") {
        toast.info("Cancelled");
        return;
      }
    }
    try {
      await axios.post(
        `${API}/admin/sync-franpos`,
        {},
        {
          headers: { "X-Admin-Password": adminPassword },
          params: { wipe_first: wipeFirst, force_full: wipeFirst },
        }
      );
      toast.info(wipeFirst ? "Wipe & full sync started" : "Delta sync started");
      // Poll every 2 seconds until done
      const poll = async () => {
        const done = await pollFranposSyncStatus();
        if (!done) setTimeout(poll, 2000);
      };
      setTimeout(poll, 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start Franpos sync");
    }
  };


  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/status`,
        { status: newStatus },
        { headers: { "X-Admin-Password": adminPassword } }
      );
      toast.success(`Order status updated to ${newStatus}`);
      fetchAllData(adminPassword);
    } catch (e) {
      toast.error("Failed to update order status");
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { "X-Admin-Password": adminPassword },
      });
      setSettings(response.data);
      // Initialize draft with non-sensitive values (sensitive stay blank for security)
      const draft = {};
      Object.entries(response.data).forEach(([k, meta]) => {
        if (meta.sensitive) {
          draft[k] = ""; // user must re-type to change
        } else {
          draft[k] = meta.value;
        }
      });
      setSettingsDraft(draft);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load settings");
    }
  };

  const saveSettings = async () => {
    setIsSavingSettings(true);
    try {
      // Only send fields that differ from displayed value
      const payload = {};
      Object.entries(settingsDraft).forEach(([k, v]) => {
        const current = settings?.[k];
        if (!current) return;
        if (current.sensitive) {
          if (v && v.trim() !== "") payload[k] = v;
        } else if (v !== current.value) {
          payload[k] = v;
        }
      });
      if (Object.keys(payload).length === 0) {
        toast.info("No changes to save");
        setIsSavingSettings(false);
        return;
      }
      const response = await axios.put(`${API}/admin/settings`, payload, {
        headers: { "X-Admin-Password": adminPassword },
      });
      toast.success(`Updated: ${response.data.updated.join(", ")}`);
      await fetchSettings();
      // refresh sync status (so google/clover configured flags update)
      const status = await axios.get(`${API}/admin/sync-status`, {
        headers: { "X-Admin-Password": adminPassword },
      });
      setSyncStatus(status.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save settings");
    } finally {
      setIsSavingSettings(false);
    }
  };

  const updateProductStock = async (productId, newStock) => {
    try {
      await axios.put(
        `${API}/admin/products/${productId}/stock?stock=${newStock}`,
        {},
        { headers: { "X-Admin-Password": adminPassword } }
      );
      toast.success("Stock updated");
      fetchAllData(adminPassword);
    } catch (e) {
      toast.error("Failed to update stock");
    }
  };

  // Product CRUD handlers
  const handleCreateProduct = async () => {
    if (!newProduct.name || !newProduct.price || !newProduct.category) {
      toast.error("Please fill in name, price, and category");
      return;
    }
    try {
      await axios.post(
        `${API}/admin/products`,
        {
          ...newProduct,
          price: parseFloat(newProduct.price),
          stock: parseInt(newProduct.stock) || 10,
          image_url: newProduct.image_url || "https://images.unsplash.com/photo-1560024802-c62d4f838bcf?w=500",
        },
        { headers: { "X-Admin-Password": adminPassword } }
      );
      toast.success("Product created successfully");
      setShowAddProduct(false);
      setNewProduct({ name: "", description: "", price: "", category: "", image_url: "", stock: 10, featured: false });
      fetchAllData(adminPassword);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create product");
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct) return;
    try {
      await axios.put(
        `${API}/admin/products/${editingProduct.id}`,
        {
          name: editingProduct.name,
          description: editingProduct.description,
          price: parseFloat(editingProduct.price),
          category: editingProduct.category,
          image_url: editingProduct.image_url,
          stock: parseInt(editingProduct.stock),
          featured: editingProduct.featured,
        },
        { headers: { "X-Admin-Password": adminPassword } }
      );
      toast.success("Product updated successfully");
      setShowEditProduct(false);
      setEditingProduct(null);
      fetchAllData(adminPassword);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update product");
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { "X-Admin-Password": adminPassword },
      });
      toast.success("Product deleted");
      fetchAllData(adminPassword);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete product");
    }
  };

  // Category CRUD handlers
  const handleCreateCategory = async () => {
    if (!newCategory.name || !newCategory.slug) {
      toast.error("Please fill in name and slug");
      return;
    }
    try {
      await axios.post(`${API}/admin/categories`, newCategory, {
        headers: { "X-Admin-Password": adminPassword },
      });
      toast.success("Category created successfully");
      setShowAddCategory(false);
      setNewCategory({ name: "", slug: "", description: "" });
      fetchAllData(adminPassword);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create category");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`, {
        headers: { "X-Admin-Password": adminPassword },
      });
      toast.success("Category deleted");
      fetchAllData(adminPassword);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete category. Make sure no products use this category.");
    }
  };

  // Move product to different category
  const handleMoveProduct = async () => {
    if (!movingProduct || !moveToCategory) {
      toast.error("Please select a category");
      return;
    }
    try {
      await axios.put(
        `${API}/admin/products/${movingProduct.id}`,
        { category: moveToCategory },
        { headers: { "X-Admin-Password": adminPassword } }
      );
      toast.success(`Product moved to ${categories.find(c => (c.slug || c.id) === moveToCategory)?.name || moveToCategory}`);
      setShowMoveProduct(false);
      setMovingProduct(null);
      setMoveToCategory("");
      fetchAllData(adminPassword);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to move product");
    }
  };

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Get products for a specific category
  const getProductsByCategory = (categorySlug) => {
    return products.filter(p => p.category === categorySlug);
  };

  // Open Add Product modal with pre-selected category
  const openAddProductForCategory = (categorySlug) => {
    setNewProduct(prev => ({ ...prev, category: categorySlug }));
    setShowAddProduct(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-500/20 text-yellow-500 border-yellow-500/30";
      case "paid":
        return "bg-blue-500/20 text-blue-500 border-blue-500/30";
      case "confirmed":
        return "bg-purple-500/20 text-purple-500 border-purple-500/30";
      case "ready":
        return "bg-cyan-500/20 text-cyan-500 border-cyan-500/30";
      case "completed":
        return "bg-green-500/20 text-green-500 border-green-500/30";
      case "cancelled":
        return "bg-red-500/20 text-red-500 border-red-500/30";
      default:
        return "bg-gray-500/20 text-gray-500 border-gray-500/30";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <Clock className="w-4 h-4" />;
      case "paid":
        return <DollarSign className="w-4 h-4" />;
      case "confirmed":
        return <Check className="w-4 h-4" />;
      case "ready":
        return <Truck className="w-4 h-4" />;
      case "completed":
        return <Check className="w-4 h-4" />;
      case "cancelled":
        return <X className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="admin-login">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md p-8"
        >
          <div className="bg-card rounded-2xl border border-border p-8 neon-border">
            <h1 className="font-unbounded text-2xl font-bold text-foreground text-center mb-2">
              Admin Access
            </h1>
            <p className="font-outfit text-sm text-muted-foreground text-center mb-8">
              Enter your admin password to continue
            </p>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label htmlFor="password" className="font-outfit">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="admin-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  required
                  className="mt-2 bg-muted border-border focus:border-primary"
                />
              </div>

              <Button
                type="submit"
                data-testid="admin-login-btn"
                disabled={loading}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-outfit font-semibold py-6 text-lg rounded-xl"
              >
                {loading ? "Verifying..." : "Access Dashboard"}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-unbounded text-3xl font-bold text-foreground">
              Admin Dashboard
            </h1>
            <p className="font-outfit text-muted-foreground">
              Manage orders, inventory, and analytics
            </p>
          </div>
          <Button
            data-testid="admin-logout-btn"
            variant="outline"
            onClick={handleLogout}
            className="border-border hover:bg-muted"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card rounded-xl border border-border p-6"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-outfit text-sm text-muted-foreground">
                    Total Revenue
                  </p>
                  <p className="font-unbounded text-2xl font-bold text-foreground">
                    ${analytics.total_revenue.toFixed(2)}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card rounded-xl border border-border p-6"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                  <ShoppingCart className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <p className="font-outfit text-sm text-muted-foreground">
                    Total Orders
                  </p>
                  <p className="font-unbounded text-2xl font-bold text-foreground">
                    {analytics.total_orders}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-card rounded-xl border border-border p-6"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-500" />
                </div>
                <div>
                  <p className="font-outfit text-sm text-muted-foreground">
                    Pending Orders
                  </p>
                  <p className="font-unbounded text-2xl font-bold text-foreground">
                    {analytics.pending_orders}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-card rounded-xl border border-border p-6"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="font-outfit text-sm text-muted-foreground">
                    7-Day Revenue
                  </p>
                  <p className="font-unbounded text-2xl font-bold text-foreground">
                    ${analytics.recent_revenue.toFixed(2)}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs defaultValue="orders" className="space-y-6">
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="orders" className="data-[state=active]:bg-primary/10">
              <ShoppingCart className="w-4 h-4 mr-2" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="products" className="data-[state=active]:bg-primary/10">
              <Package className="w-4 h-4 mr-2" />
              Products
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-primary/10">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-primary/10" data-testid="tab-settings">
              <SettingsIcon className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <div className="bg-card rounded-xl border border-border">
              <div className="p-6 border-b border-border flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-unbounded text-xl font-semibold text-foreground">
                    Order Management
                  </h2>
                  <p className="font-outfit text-sm text-muted-foreground">
                    View and manage customer orders
                  </p>
                </div>
                {orders.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={handleClearAllOrders}
                    disabled={isClearingOrders}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500"
                    data-testid="clear-all-orders-btn"
                  >
                    <Trash2 className={`w-4 h-4 mr-2 ${isClearingOrders ? "animate-pulse" : ""}`} />
                    {isClearingOrders ? "Clearing..." : `Clear all orders (${orders.length})`}
                  </Button>
                )}
              </div>

              <div className="divide-y divide-border">
                {orders.length === 0 ? (
                  <div className="p-12 text-center">
                    <ShoppingCart className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="font-outfit text-muted-foreground">
                      No orders yet
                    </p>
                  </div>
                ) : (
                  orders.map((order) => (
                    <div
                      key={order.id}
                      className="p-6 hover:bg-muted/50 transition-colors"
                      data-testid={`order-row-${order.id}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-unbounded font-semibold text-foreground">
                              #{order.id.slice(0, 8).toUpperCase()}
                            </span>
                            <Badge
                              variant="outline"
                              className={getStatusColor(order.status)}
                            >
                              {getStatusIcon(order.status)}
                              <span className="ml-1 capitalize">{order.status}</span>
                            </Badge>
                          </div>
                          <p className="font-outfit text-sm text-muted-foreground">
                            {order.customer_name} • {order.customer_email} •{" "}
                            {order.customer_phone}
                          </p>
                          <p className="font-outfit text-xs text-muted-foreground mt-1">
                            {order.items?.length || 0} items • Created:{" "}
                            {new Date(order.created_at).toLocaleString()}
                          </p>
                          {order.pickup_time && (
                            <p className="font-outfit text-xs text-primary mt-1">
                              Pickup: {order.pickup_time}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-outfit text-xs text-muted-foreground">
                              Subtotal: ${order.subtotal?.toFixed(2) || "0.00"}
                            </p>
                            <p className="font-outfit text-xs text-muted-foreground">
                              Tax: ${order.tax?.toFixed(2) || "0.00"}
                            </p>
                            <p className="font-unbounded text-lg font-bold text-primary">
                              ${order.total?.toFixed(2) || "0.00"}
                            </p>
                          </div>

                          <Select
                            value={order.status}
                            onValueChange={(value) =>
                              updateOrderStatus(order.id, value)
                            }
                          >
                            <SelectTrigger className="w-36 bg-muted border-border">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="paid">Paid</SelectItem>
                              <SelectItem value="confirmed">Confirmed</SelectItem>
                              <SelectItem value="ready">Ready</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="bg-card rounded-xl border border-border">
              <div className="p-6 border-b border-border">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h2 className="font-unbounded text-xl font-semibold text-foreground">
                      Inventory Management
                    </h2>
                    <p className="font-outfit text-sm text-muted-foreground">
                      Manage categories and products - Click a category to view/add products
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Clover Sync Section */}
                    {syncStatus?.clover_configured && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          onClick={handleCloverSync}
                          disabled={isSyncing}
                          className="border-border hover:bg-cyan-500/10 hover:border-cyan-500 hover:text-cyan-500"
                          data-testid="sync-clover-btn"
                        >
                          <Cloud className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-pulse' : ''}`} />
                          <RefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
                          {isSyncing ? "Syncing..." : "Sync from Clover"}
                        </Button>
                        {syncStatus?.last_sync && (
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            Last sync: {new Date(syncStatus.last_sync).toLocaleString()}
                          </span>
                        )}
                      </div>
                    )}
                    {syncStatus?.franpos_configured && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          onClick={() => handleFranposSync(false)}
                          disabled={franposSync?.running}
                          className="border-border hover:bg-orange-500/10 hover:border-orange-500 hover:text-orange-500"
                          data-testid="sync-franpos-btn"
                          title="Delta sync — only fetches products changed since last sync (cheap on quota)"
                        >
                          <RefreshCw className={`w-4 h-4 mr-2 ${franposSync?.running ? 'animate-spin' : ''}`} />
                          {franposSync?.running
                            ? `Syncing… ${franposSync.synced}/${franposSync.total}`
                            : "Sync from Franpos"}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleFranposSync(true)}
                          disabled={franposSync?.running}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500"
                          data-testid="wipe-and-sync-franpos-btn"
                          title="Wipe all products & categories, then re-sync fresh from Franpos"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Wipe & Full Sync
                        </Button>
                        {franposQuota?.remaining != null && (
                          <span
                            className={`text-xs whitespace-nowrap font-outfit ${franposQuota.remaining < 100 ? 'text-red-400' : 'text-muted-foreground'}`}
                            title="Franpos API quota remaining this month"
                          >
                            Quota: {franposQuota.remaining}/1000
                          </span>
                        )}
                      </div>
                    )}
                    {syncStatus?.google_images_configured && (
                      <Button
                        variant="outline"
                        onClick={handleAutoFetchAllImages}
                        disabled={isBulkFetching}
                        className="border-border hover:bg-purple-500/10 hover:border-purple-500 hover:text-purple-500"
                        data-testid="auto-fetch-images-btn"
                        title="Fetch images from Google for all products with placeholder images"
                      >
                        <Wand2 className={`w-4 h-4 mr-2 ${isBulkFetching ? 'animate-pulse' : ''}`} />
                        {isBulkFetching ? "Fetching images..." : "Auto-fetch images"}
                      </Button>
                    )}
                    <Dialog open={showAddCategory} onOpenChange={setShowAddCategory}>
                      <DialogTrigger asChild>
                        <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-category-btn">
                          <FolderPlus className="w-4 h-4 mr-2" />
                          Add Category
                        </Button>
                      </DialogTrigger>
                  <DialogContent className="bg-card border-border">
                    <DialogHeader>
                      <DialogTitle className="font-unbounded">Add New Category</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label htmlFor="cat-name">Category Name</Label>
                        <Input
                          id="cat-name"
                          data-testid="category-name-input"
                          value={newCategory.name}
                          onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                          placeholder="e.g., Premium Devices"
                          className="mt-1 bg-muted border-border"
                        />
                      </div>
                      <div>
                        <Label htmlFor="cat-slug">Slug (URL-friendly)</Label>
                        <Input
                          id="cat-slug"
                          data-testid="category-slug-input"
                          value={newCategory.slug}
                          onChange={(e) => setNewCategory({ ...newCategory, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                          placeholder="e.g., premium-devices"
                          className="mt-1 bg-muted border-border"
                        />
                      </div>
                      <div>
                        <Label htmlFor="cat-desc">Description (Optional)</Label>
                        <Input
                          id="cat-desc"
                          value={newCategory.description}
                          onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                          placeholder="Brief description"
                          className="mt-1 bg-muted border-border"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button variant="outline" className="border-border">Cancel</Button>
                      </DialogClose>
                      <Button onClick={handleCreateCategory} data-testid="save-category-btn" className="bg-primary text-primary-foreground">
                        <Save className="w-4 h-4 mr-2" />
                        Save Category
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                  </div>
                </div>
              </div>

              {/* Categories with Products */}
              <div className="divide-y divide-border">
                {categories.length === 0 ? (
                  <div className="p-12 text-center">
                    <Folder className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="font-outfit text-muted-foreground mb-4">
                      No categories yet. Create your first category to start adding products.
                    </p>
                  </div>
                ) : (
                  categories.map((cat) => {
                    const categoryProducts = getProductsByCategory(cat.slug || cat.id);
                    const isExpanded = expandedCategories[cat.id || cat.slug];
                    
                    return (
                      <div key={cat.id || cat.slug} className="bg-card" data-testid={`category-${cat.slug || cat.id}`}>
                        {/* Category Header */}
                        <div 
                          className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => toggleCategory(cat.id || cat.slug)}
                        >
                          <div className="flex items-center gap-3">
                            {isExpanded ? (
                              <ChevronDown className="w-5 h-5 text-primary" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-muted-foreground" />
                            )}
                            <Folder className={`w-5 h-5 ${isExpanded ? 'text-primary' : 'text-muted-foreground'}`} />
                            <div>
                              <h3 className="font-unbounded font-semibold text-foreground">
                                {cat.name}
                              </h3>
                              <p className="font-outfit text-xs text-muted-foreground">
                                {categoryProducts.length} products • {cat.description || 'No description'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <div
                              className="flex items-center gap-2 mr-2 px-3 py-1.5 rounded-lg bg-muted/40 border border-border"
                              title={cat.hidden ? "Hidden from website — toggle to show" : "Visible on website — toggle to hide"}
                            >
                              <span className={`text-xs font-outfit ${cat.hidden ? 'text-muted-foreground' : 'text-cyan-400'}`}>
                                {cat.hidden ? 'Hidden' : 'Visible'}
                              </span>
                              <Switch
                                checked={!cat.hidden}
                                onCheckedChange={(checked) => toggleCategoryVisibility(cat.id || cat.slug, !checked)}
                                data-testid={`category-visibility-${cat.slug || cat.id}`}
                              />
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-border hover:bg-primary/10 hover:text-primary"
                              onClick={() => openAddProductForCategory(cat.slug || cat.id)}
                              data-testid={`add-product-to-${cat.slug || cat.id}`}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add Product
                            </Button>
                            <Button
                              size="icon"
                              variant="outline"
                              className="w-8 h-8 border-border hover:border-red-500 hover:text-red-500"
                              onClick={() => handleDeleteCategory(cat.id || cat.slug)}
                              data-testid={`delete-category-${cat.slug || cat.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Products List (Expandable) */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="bg-muted/20 border-t border-border">
                                {categoryProducts.length === 0 ? (
                                  <div className="p-8 text-center">
                                    <Package className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                                    <p className="font-outfit text-sm text-muted-foreground">
                                      No products in this category yet
                                    </p>
                                    <Button
                                      size="sm"
                                      className="mt-3 bg-primary text-primary-foreground"
                                      onClick={() => openAddProductForCategory(cat.slug || cat.id)}
                                    >
                                      <Plus className="w-4 h-4 mr-1" />
                                      Add First Product
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="divide-y divide-border/50">
                                    {categoryProducts.map((product) => (
                                      <div
                                        key={product.id}
                                        className="p-4 pl-12 flex items-center gap-4 hover:bg-muted/50 transition-colors"
                                        data-testid={`product-row-${product.id}`}
                                      >
                                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                                          <img
                                            src={product.image_url}
                                            alt={product.name}
                                            className="w-full h-full object-cover"
                                          />
                                        </div>

                                        <div className="flex-1 min-w-0">
                                          <h4 className="font-outfit font-medium text-foreground truncate">
                                            {product.name}
                                          </h4>
                                          <p className="font-outfit text-sm text-muted-foreground">
                                            ${product.price.toFixed(2)}
                                          </p>
                                        </div>

                                        <div className="flex items-center gap-3">
                                          {/* Stock indicator */}
                                          <div
                                            className={`text-center px-2 py-1 rounded-lg text-xs ${
                                              product.stock <= 5
                                                ? "bg-red-500/10 text-red-500"
                                                : product.stock <= 10
                                                ? "bg-yellow-500/10 text-yellow-500"
                                                : "bg-green-500/10 text-green-500"
                                            }`}
                                          >
                                            {product.stock} in stock
                                          </div>

                                          {/* Stock controls */}
                                          <div className="flex items-center gap-1">
                                            <Button
                                              variant="outline"
                                              size="icon"
                                              className="w-7 h-7 border-border"
                                              onClick={() => updateProductStock(product.id, Math.max(0, product.stock - 1))}
                                            >
                                              <Minus className="w-3 h-3" />
                                            </Button>
                                            <Input
                                              type="number"
                                              value={product.stock}
                                              onChange={(e) => updateProductStock(product.id, parseInt(e.target.value) || 0)}
                                              className="w-14 h-7 text-center text-sm bg-muted border-border"
                                            />
                                            <Button
                                              variant="outline"
                                              size="icon"
                                              className="w-7 h-7 border-border"
                                              onClick={() => updateProductStock(product.id, product.stock + 1)}
                                            >
                                              <Plus className="w-3 h-3" />
                                            </Button>
                                          </div>

                                          {/* Move to different category */}
                                          <Button
                                            variant="outline"
                                            size="icon"
                                            className="w-8 h-8 border-border hover:border-blue-500 hover:text-blue-500"
                                            onClick={() => {
                                              setMovingProduct(product);
                                              setMoveToCategory("");
                                              setShowMoveProduct(true);
                                            }}
                                            title="Move to another category"
                                            data-testid={`move-product-${product.id}`}
                                          >
                                            <MoveRight className="w-4 h-4" />
                                          </Button>

                                          {/* Auto-fetch image from Google */}
                                          {syncStatus?.google_images_configured && (
                                            <Button
                                              variant="outline"
                                              size="icon"
                                              className="w-8 h-8 border-border hover:border-purple-500 hover:text-purple-500"
                                              onClick={() => handleAutoFetchImage(product.id)}
                                              disabled={fetchingImageId === product.id}
                                              title="Auto-fetch image from Google"
                                              data-testid={`auto-image-${product.id}`}
                                            >
                                              <Wand2 className={`w-4 h-4 ${fetchingImageId === product.id ? 'animate-pulse' : ''}`} />
                                            </Button>
                                          )}

                                          {/* Edit */}
                                          <Button
                                            variant="outline"
                                            size="icon"
                                            className="w-8 h-8 border-border hover:border-primary"
                                            onClick={() => {
                                              setEditingProduct({ ...product });
                                              setShowEditProduct(true);
                                            }}
                                            data-testid={`edit-product-${product.id}`}
                                          >
                                            <Edit className="w-4 h-4" />
                                          </Button>

                                          {/* Delete */}
                                          <Button
                                            variant="outline"
                                            size="icon"
                                            className="w-8 h-8 border-border hover:border-red-500 hover:text-red-500"
                                            onClick={() => handleDeleteProduct(product.id)}
                                            data-testid={`delete-product-${product.id}`}
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Add Product Dialog */}
            <Dialog open={showAddProduct} onOpenChange={setShowAddProduct}>
              <DialogContent className="bg-card border-border max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-unbounded">
                    Add Product to {categories.find(c => (c.slug || c.id) === newProduct.category)?.name || 'Category'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                  <div>
                    <Label htmlFor="prod-name">Product Name *</Label>
                    <Input
                      id="prod-name"
                      data-testid="product-name-input"
                      value={newProduct.name}
                      onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                      placeholder="e.g., Vapor Pro X"
                      className="mt-1 bg-muted border-border"
                    />
                  </div>
                  <div>
                    <Label htmlFor="prod-desc">Description</Label>
                    <Textarea
                      id="prod-desc"
                      data-testid="product-desc-input"
                      value={newProduct.description}
                      onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                      placeholder="Product description..."
                      className="mt-1 bg-muted border-border min-h-[80px]"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="prod-price">Price ($) *</Label>
                      <Input
                        id="prod-price"
                        type="number"
                        step="0.01"
                        data-testid="product-price-input"
                        value={newProduct.price}
                        onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                        placeholder="29.99"
                        className="mt-1 bg-muted border-border"
                      />
                    </div>
                    <div>
                      <Label htmlFor="prod-stock">Stock</Label>
                      <Input
                        id="prod-stock"
                        type="number"
                        data-testid="product-stock-input"
                        value={newProduct.stock}
                        onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })}
                        placeholder="10"
                        className="mt-1 bg-muted border-border"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="prod-category">Category *</Label>
                    <Select
                      value={newProduct.category}
                      onValueChange={(value) => setNewProduct({ ...newProduct, category: value })}
                    >
                      <SelectTrigger className="mt-1 bg-muted border-border" data-testid="product-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id || cat.slug} value={cat.slug || cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="prod-image">Image URL</Label>
                    <Input
                      id="prod-image"
                      data-testid="product-image-input"
                      value={newProduct.image_url}
                      onChange={(e) => setNewProduct({ ...newProduct, image_url: e.target.value })}
                      placeholder="https://..."
                      className="mt-1 bg-muted border-border"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Leave empty for default image</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="prod-featured"
                      checked={newProduct.featured}
                      onChange={(e) => setNewProduct({ ...newProduct, featured: e.target.checked })}
                      className="rounded border-border"
                    />
                    <Label htmlFor="prod-featured" className="cursor-pointer">Featured product</Label>
                  </div>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline" className="border-border">Cancel</Button>
                  </DialogClose>
                  <Button onClick={handleCreateProduct} data-testid="save-product-btn" className="bg-primary text-primary-foreground">
                    <Save className="w-4 h-4 mr-2" />
                    Save Product
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Edit Product Dialog */}
            <Dialog open={showEditProduct} onOpenChange={setShowEditProduct}>
              <DialogContent className="bg-card border-border max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-unbounded">Edit Product</DialogTitle>
                </DialogHeader>
                {editingProduct && (
                  <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                    <div>
                      <Label htmlFor="edit-prod-name">Product Name</Label>
                      <Input
                        id="edit-prod-name"
                        value={editingProduct.name}
                        onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                        className="mt-1 bg-muted border-border"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-prod-desc">Description</Label>
                      <Textarea
                        id="edit-prod-desc"
                        value={editingProduct.description}
                        onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
                        className="mt-1 bg-muted border-border min-h-[80px]"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="edit-prod-price">Price ($)</Label>
                        <Input
                          id="edit-prod-price"
                          type="number"
                          step="0.01"
                          value={editingProduct.price}
                          onChange={(e) => setEditingProduct({ ...editingProduct, price: e.target.value })}
                          className="mt-1 bg-muted border-border"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-prod-stock">Stock</Label>
                        <Input
                          id="edit-prod-stock"
                          type="number"
                          value={editingProduct.stock}
                          onChange={(e) => setEditingProduct({ ...editingProduct, stock: e.target.value })}
                          className="mt-1 bg-muted border-border"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="edit-prod-category">Category (Change to move product)</Label>
                      <Select
                        value={editingProduct.category}
                        onValueChange={(value) => setEditingProduct({ ...editingProduct, category: value })}
                      >
                        <SelectTrigger className="mt-1 bg-muted border-border">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id || cat.slug} value={cat.slug || cat.id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="edit-prod-image">Image URL</Label>
                      <Input
                        id="edit-prod-image"
                        value={editingProduct.image_url}
                        onChange={(e) => setEditingProduct({ ...editingProduct, image_url: e.target.value })}
                        className="mt-1 bg-muted border-border"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="edit-prod-featured"
                        checked={editingProduct.featured}
                        onChange={(e) => setEditingProduct({ ...editingProduct, featured: e.target.checked })}
                        className="rounded border-border"
                      />
                      <Label htmlFor="edit-prod-featured" className="cursor-pointer">Featured product</Label>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline" className="border-border">Cancel</Button>
                  </DialogClose>
                  <Button onClick={handleUpdateProduct} className="bg-primary text-primary-foreground">
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Move Product Dialog */}
            <Dialog open={showMoveProduct} onOpenChange={setShowMoveProduct}>
              <DialogContent className="bg-card border-border">
                <DialogHeader>
                  <DialogTitle className="font-unbounded">Move Product to Category</DialogTitle>
                </DialogHeader>
                {movingProduct && (
                  <div className="space-y-4 py-4">
                    <p className="font-outfit text-muted-foreground">
                      Moving <span className="text-foreground font-medium">{movingProduct.name}</span> from{" "}
                      <span className="text-foreground font-medium">
                        {categories.find(c => (c.slug || c.id) === movingProduct.category)?.name || movingProduct.category}
                      </span>
                    </p>
                    <div>
                      <Label>Select New Category</Label>
                      <Select value={moveToCategory} onValueChange={setMoveToCategory}>
                        <SelectTrigger className="mt-1 bg-muted border-border">
                          <SelectValue placeholder="Choose category..." />
                        </SelectTrigger>
                        <SelectContent>
                          {categories
                            .filter(cat => (cat.slug || cat.id) !== movingProduct.category)
                            .map((cat) => (
                              <SelectItem key={cat.id || cat.slug} value={cat.slug || cat.id}>
                                {cat.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline" className="border-border">Cancel</Button>
                  </DialogClose>
                  <Button onClick={handleMoveProduct} className="bg-primary text-primary-foreground">
                    <MoveRight className="w-4 h-4 mr-2" />
                    Move Product
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            {analytics && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Status Breakdown */}
                <div className="bg-card rounded-xl border border-border p-6">
                  <h3 className="font-unbounded text-lg font-semibold text-foreground mb-4">
                    Order Status Breakdown
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(analytics.status_breakdown).map(
                      ([status, count]) => (
                        <div
                          key={status}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2">
                            {getStatusIcon(status)}
                            <span className="font-outfit capitalize text-foreground">
                              {status}
                            </span>
                          </div>
                          <Badge variant="outline" className={getStatusColor(status)}>
                            {count}
                          </Badge>
                        </div>
                      )
                    )}
                  </div>
                </div>

                {/* Top Products */}
                <div className="bg-card rounded-xl border border-border p-6">
                  <h3 className="font-unbounded text-lg font-semibold text-foreground mb-4">
                    Top Selling Products
                  </h3>
                  {analytics.top_products.length === 0 ? (
                    <p className="font-outfit text-sm text-muted-foreground">
                      No sales data yet
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {analytics.top_products.map((product, index) => (
                        <div
                          key={product.name}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                              {index + 1}
                            </span>
                            <span className="font-outfit text-foreground">
                              {product.name}
                            </span>
                          </div>
                          <span className="font-outfit text-sm text-muted-foreground">
                            {product.quantity} sold
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Low Stock Alert */}
                <div className="bg-card rounded-xl border border-border p-6">
                  <h3 className="font-unbounded text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-500" />
                    Low Stock Alert
                  </h3>
                  {analytics.low_stock_products.length === 0 ? (
                    <p className="font-outfit text-sm text-green-500">
                      All products well stocked!
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {analytics.low_stock_products.map((product) => (
                        <div
                          key={product.id}
                          className="flex items-center justify-between"
                        >
                          <span className="font-outfit text-foreground">
                            {product.name}
                          </span>
                          <Badge
                            variant="outline"
                            className="bg-red-500/10 text-red-500 border-red-500/30"
                          >
                            {product.stock} left
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Tax Info */}
                <div className="bg-card rounded-xl border border-border p-6">
                  <h3 className="font-unbounded text-lg font-semibold text-foreground mb-4">
                    Tax Configuration
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="font-outfit text-muted-foreground">
                        Current Tax Rate
                      </span>
                      <span className="font-unbounded text-xl font-bold text-primary">
                        {(analytics.tax_rate * 100).toFixed(2)}%
                      </span>
                    </div>
                    <p className="font-outfit text-xs text-muted-foreground">
                      Kansas state tax rate applied to all orders
                    </p>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-saira text-2xl">App Settings</CardTitle>
                  <CardDescription className="font-outfit">
                    Configure Clover POS, Google Custom Search, and tax rate. Sensitive fields (tokens/keys) show only the last 4 characters — leave blank when editing to keep the existing value.
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  onClick={fetchSettings}
                  data-testid="reload-settings-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reload
                </Button>
              </CardHeader>
              <CardContent className="space-y-8">
                {!settings ? (
                  <div className="text-center py-12">
                    <Button onClick={fetchSettings} data-testid="load-settings-btn">
                      <SettingsIcon className="w-4 h-4 mr-2" />
                      Load Settings
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Clover Section */}
                    <div className="space-y-4">
                      <h3 className="font-saira text-lg text-cyan-400 border-b border-border pb-2">Clover POS</h3>
                      {[
                        { key: "CLOVER_MERCHANT_ID", label: "Merchant ID", desc: "Your Clover merchant ID" },
                        { key: "CLOVER_API_TOKEN", label: "API Token (private)", desc: "Server-side payment token. Get from Clover developer dashboard." },
                        { key: "CLOVER_PUBLIC_KEY", label: "Public Key", desc: "Used by checkout iframe (safe to expose)" },
                        { key: "CLOVER_REST_TOKEN", label: "REST API Token", desc: "For inventory and category sync. Needs Items & Inventory read permission." },
                        { key: "CLOVER_API_BASE_URL", label: "API Base URL", desc: "https://scl.clover.com (sandbox) or https://api.clover.com (production)" },
                      ].map(({ key, label, desc }) => {
                        const meta = settings[key];
                        if (!meta) return null;
                        return (
                          <div key={key} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                            <Label className="font-saira mt-2">{label}</Label>
                            <div className="md:col-span-2 space-y-1">
                              <Input
                                type={meta.sensitive ? "password" : "text"}
                                value={settingsDraft[key] ?? ""}
                                onChange={(e) => setSettingsDraft({ ...settingsDraft, [key]: e.target.value })}
                                placeholder={meta.sensitive && meta.is_set ? `Currently set: ${meta.value} — leave blank to keep` : ""}
                                className="bg-secondary border-border"
                                data-testid={`setting-${key}`}
                              />
                              <p className="text-xs text-muted-foreground font-outfit">{desc}</p>
                            </div>
                          </div>
                        );
                      })}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center pt-2">
                        <Label className="font-saira">Payment Test Mode</Label>
                        <div className="md:col-span-2 flex items-center gap-3">
                          <Switch
                            checked={!!settingsDraft.PAYMENT_TEST_MODE}
                            onCheckedChange={(v) => setSettingsDraft({ ...settingsDraft, PAYMENT_TEST_MODE: v })}
                            data-testid="setting-PAYMENT_TEST_MODE"
                          />
                          <span className="text-sm text-muted-foreground font-outfit">
                            {settingsDraft.PAYMENT_TEST_MODE ? "ON — orders simulated, no real charges" : "OFF — live payments via Clover"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Franpos POS Section */}
                    <div className="space-y-4">
                      <h3 className="font-saira text-lg text-orange-400 border-b border-border pb-2">Franpos POS (SumUp-POS) — Inventory Source</h3>
                      {[
                        { key: "FRANPOS_TOKEN", label: "API Token", desc: "Long hex token from sumup-pos.com → Settings → Public API" },
                        { key: "FRANPOS_LOCATION_ID", label: "Location ID", desc: "Numeric location ID (e.g. 207162)" },
                        { key: "FRANPOS_API_BASE_URL", label: "API Base URL", desc: "Default: https://publicapi.franpos.com" },
                      ].map(({ key, label, desc }) => {
                        const meta = settings[key];
                        if (!meta) return null;
                        return (
                          <div key={key} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                            <Label className="font-saira mt-2">{label}</Label>
                            <div className="md:col-span-2 space-y-1">
                              <Input
                                type={meta.sensitive ? "password" : "text"}
                                value={settingsDraft[key] ?? ""}
                                onChange={(e) => setSettingsDraft({ ...settingsDraft, [key]: e.target.value })}
                                placeholder={meta.sensitive && meta.is_set ? `Currently set: ${meta.value} — leave blank to keep` : ""}
                                className="bg-secondary border-border"
                                data-testid={`setting-${key}`}
                              />
                              <p className="text-xs text-muted-foreground font-outfit">{desc}</p>
                            </div>
                          </div>
                        );
                      })}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                        <Label className="font-saira mt-2">Active Product Sync Source</Label>
                        <div className="md:col-span-2 space-y-1">
                          <select
                            value={settingsDraft.PRODUCT_SYNC_SOURCE ?? "clover"}
                            onChange={(e) => setSettingsDraft({ ...settingsDraft, PRODUCT_SYNC_SOURCE: e.target.value })}
                            className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-foreground font-outfit"
                            data-testid="setting-PRODUCT_SYNC_SOURCE"
                          >
                            <option value="clover">Clover (current)</option>
                            <option value="franpos">Franpos / SumUp-POS</option>
                          </select>
                          <p className="text-xs text-muted-foreground font-outfit">
                            Which POS system to pull products and categories from on the hourly sync. Payments continue to use Clover regardless of this setting.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Google Section */}
                    <div className="space-y-4">
                      <h3 className="font-saira text-lg text-purple-400 border-b border-border pb-2">Google Custom Search (Product Images)</h3>
                      {[
                        { key: "GOOGLE_API_KEY", label: "API Key", desc: "From console.cloud.google.com/apis/credentials" },
                        { key: "GOOGLE_CSE_ID", label: "Search Engine ID", desc: "From programmablesearchengine.google.com/controlpanel/all" },
                      ].map(({ key, label, desc }) => {
                        const meta = settings[key];
                        if (!meta) return null;
                        return (
                          <div key={key} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                            <Label className="font-saira mt-2">{label}</Label>
                            <div className="md:col-span-2 space-y-1">
                              <Input
                                type={meta.sensitive ? "password" : "text"}
                                value={settingsDraft[key] ?? ""}
                                onChange={(e) => setSettingsDraft({ ...settingsDraft, [key]: e.target.value })}
                                placeholder={meta.sensitive && meta.is_set ? `Currently set: ${meta.value} — leave blank to keep` : ""}
                                className="bg-secondary border-border"
                                data-testid={`setting-${key}`}
                              />
                              <p className="text-xs text-muted-foreground font-outfit">{desc}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Tax Section */}
                    <div className="space-y-4">
                      <h3 className="font-saira text-lg text-green-400 border-b border-border pb-2">Tax</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                        <Label className="font-saira mt-2">Tax Rate (decimal)</Label>
                        <div className="md:col-span-2 space-y-1">
                          <Input
                            type="number"
                            step="0.0001"
                            value={settingsDraft.TAX_RATE ?? ""}
                            onChange={(e) => setSettingsDraft({ ...settingsDraft, TAX_RATE: parseFloat(e.target.value) || 0 })}
                            className="bg-secondary border-border"
                            data-testid="setting-TAX_RATE"
                          />
                          <p className="text-xs text-muted-foreground font-outfit">
                            Example: 0.0948 = 9.48%. Applied to all order subtotals.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end pt-4 border-t border-border">
                      <Button
                        onClick={saveSettings}
                        disabled={isSavingSettings}
                        className="bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="save-settings-btn"
                      >
                        <Save className={`w-4 h-4 mr-2 ${isSavingSettings ? "animate-pulse" : ""}`} />
                        {isSavingSettings ? "Saving..." : "Save Settings"}
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
